import { notification } from 'antd';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@/i18n';
import type { ApiErrorResponse } from '@/types/api';

const DEFAULT_TIMEOUT_MS = 15_000;
const CORRELATION_ID_HEADER = 'X-Correlation-ID';
const GEOCODE_PATH_PATTERN = /\/api\/v1\/(geocode|reverse-geocode)/;
const QUOTA_NOTIFICATION_KEY = 'geocoder-quota-exhausted';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers[CORRELATION_ID_HEADER] = uuidv4();
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    maybeNotifyGeocoderQuota(error);
    return Promise.reject(error);
  },
);

function maybeNotifyGeocoderQuota(error: AxiosError<ApiErrorResponse>): void {
  if (error.response?.status !== 429) {
    return;
  }
  const url = error.config?.url ?? '';
  if (!GEOCODE_PATH_PATTERN.test(url)) {
    return;
  }
  // Deduplicate repeated firings (e.g., autocomplete issues many requests).
  notification.warning({
    key: QUOTA_NOTIFICATION_KEY,
    message: i18n.t('geocoder.quotaExhausted.title'),
    description: i18n.t('geocoder.quotaExhausted.description'),
    duration: 8,
  });
}

export default apiClient;
